import * as React from 'react';

import { useMutation } from '@apollo/client';

import { Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { NotificationConfig } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/types';
import { ResolutionStatus } from 'bundles/ai-coach-platform/components/patterns/suggestions/types';
import type {
  AiCoachPlatform_SaveCoachUserSettingsMutation as SaveCoachUserSettingsMutation,
  AiCoachPlatform_SaveCoachUserSettingsMutationVariables as SaveCoachUserSettingsMutationVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachSaveUserSettingsMutation';
import aiCoachSaveUserSettingsMutation from 'bundles/ai-coach-platform/queries/aiCoachSaveUserSettingsMutation.graphql';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import localStorageEx from 'bundles/common/utils/localStorageEx';

import _t from 'i18n!nls/ai-coach-platform';

export const COACH_CHAT_MEMORY_NOTIFICATION_DISMISSED = 'AiCoachPlatform_NewMemoryFeatureNotificationDismissed';
export const COACH_CHAT_MEMORY_NOTIFICATION_SHOWN = 'AiCoachPlatform_NewMemoryFeatureNotificationShown';

export const isNotificationDismissed = (): boolean | null => {
  return localStorageEx.getItem<boolean>(COACH_CHAT_MEMORY_NOTIFICATION_DISMISSED, (value) => value === 'true', false);
};

const setNotificationShown = () => {
  localStorageEx.setItem(COACH_CHAT_MEMORY_NOTIFICATION_SHOWN, true, () => 'true');
};

const isNotificationShown = (): boolean | null => {
  return localStorageEx.getItem<boolean>(COACH_CHAT_MEMORY_NOTIFICATION_SHOWN, (value) => value === 'true', false);
};

const setNotificationDismissed = () => {
  localStorageEx.setItem(COACH_CHAT_MEMORY_NOTIFICATION_DISMISSED, true, () => 'true');
};

export const useMemoryPreferenceNotification = (): NotificationConfig => {
  const track = useTracker();
  const [saveSettings] = useMutation<SaveCoachUserSettingsMutation, SaveCoachUserSettingsMutationVariables>(
    aiCoachSaveUserSettingsMutation,
    {
      context: { clientName: 'gatewayGql' },
    }
  );

  const handleEventing = React.useCallback<(enabled: boolean) => void>(
    (enabled = false) => {
      track('interact_coach_settings', {
        settingsInteractionType: `toggle_memory_${enabled ? 'on' : 'off'}`,
      });
    },
    [track]
  );

  const enableMemoryMutation = React.useCallback<() => Promise<void>>(async () => {
    try {
      handleEventing(true);
      // optimization: skip API call if we already showed the notification before
      if (!isNotificationShown()) {
        await saveSettings({
          variables: {
            input: {
              memoryPreferenceInput: {
                enableMemory: true,
              },
            },
          },
        });
        // Mark as shown in localStorage after successful mutation
        setNotificationShown();
      }
    } catch (error) {
      captureMessageInSentry({
        error,
        source: 'MemoryPreferenceNotification_onMount',
      });
    }
  }, [saveSettings, handleEventing]);

  const handleDismissAction = React.useCallback<(action: 'accept' | 'reject') => Promise<void>>(
    async (action: 'accept' | 'reject') => {
      try {
        if (action === 'reject') {
          handleEventing(false);
          setNotificationDismissed();
          await saveSettings({
            variables: {
              input: {
                memoryPreferenceInput: {
                  enableMemory: false,
                },
              },
            },
          });
        }
        setNotificationDismissed();
      } catch (error) {
        captureMessageInSentry({
          error,
          source: 'MemoryPreferenceNotification_onAction',
        });
      }
    },
    [saveSettings, handleEventing]
  );

  return {
    id: 'memory-preference-notification',
    status: ResolutionStatus.UNRESOLVED,
    title: (
      <Typography2 variant="subtitleSmall" component="h3">
        {_t('New! #{coachLabel} just got more helpful', {
          coachLabel: 'Coach',
        })}
      </Typography2>
    ),
    content: _t(
      `#{coachLabel} can now remember things you've shared, like your goals, interests, or learning preferences, to personalize your experience over time. Go to #{coachLabel} settings > Memory to learn more.`,
      {
        coachLabel: 'Coach',
      }
    ),
    acceptLabel: _t('Sounds great!'),
    dismissLabel: _t('Turn it off'),
    onAction: handleDismissAction,
    onMount: enableMemoryMutation,
    isDismissed: isNotificationDismissed() ?? false,
  };
};
